import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Settings, Search, Filter, Network, List } from 'lucide-react'
import { adminApi } from '../services/api'
import toast from 'react-hot-toast'
import FeatureDependencyVisualization from '../components/FeatureDependencyVisualization'

const Features = () => {
  const [features, setFeatures] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingFeature, setEditingFeature] = useState(null)
  const [activeTab, setActiveTab] = useState('list') // 'list' or 'dependencies'

  useEffect(() => {
    fetchFeatures()
  }, [])

  const fetchFeatures = async () => {
    try {
      setIsLoading(true)
      const response = await adminApi.getFeatures()
      setFeatures(response.data.features || [])
    } catch (error) {
      console.error('Error fetching features:', error)
      toast.error('Failed to load features')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateFeature = async (formData) => {
    try {
      await adminApi.createFeature(formData)
      toast.success('Feature created successfully')
      setShowCreateModal(false)
      fetchFeatures()
    } catch (error) {
      console.error('Error creating feature:', error)
      toast.error('Failed to create feature')
    }
  }

  const handleUpdateFeature = async (id, formData) => {
    try {
      await adminApi.updateFeature(id, formData)
      toast.success('Feature updated successfully')
      setEditingFeature(null)
      fetchFeatures()
    } catch (error) {
      console.error('Error updating feature:', error)
      toast.error('Failed to update feature')
    }
  }

  const handleDeleteFeature = async (id) => {
    if (!confirm('Are you sure you want to delete this feature?')) {
      return
    }

    try {
      await adminApi.deleteFeature(id)
      toast.success('Feature deleted successfully')
      fetchFeatures()
    } catch (error) {
      console.error('Error deleting feature:', error)
      toast.error('Failed to delete feature')
    }
  }

  const filteredFeatures = features.filter(feature => {
    const matchesSearch = feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.code.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'all' || feature.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const categories = ['all', ...new Set(features.map(f => f.category))]

  if (isLoading) {
    return (
      <div className="page-container animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Özellikler (Modüller)</h1>
          <p className="page-subtitle">Sistem özelliklerini ve bağımlılıklarını yönetin</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <Plus size={20} className="mr-2" />
          Özellik Ekle
        </button>
      </div>

      {/* Main Content Card */}
      <div className="glass-card overflow-hidden">
        {/* Tabs inside Card Header */}
        <div className="border-b border-gray-100 bg-gray-50/50 px-6 pt-4">
          <nav className="flex space-x-6">
            <button
              onClick={() => setActiveTab('list')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-gray-300'
                }`}
            >
              <List className="w-4 h-4 inline mr-2" />
              Özellik Listesi
            </button>
            <button
              onClick={() => setActiveTab('dependencies')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'dependencies'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-gray-300'
                }`}
            >
              <Network className="w-4 h-4 inline mr-2" />
              Bağımlılıklar
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'list' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Özellik ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Filter size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="input pl-10 pr-8"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category === 'all' ? 'Tüm Kategoriler' : category.charAt(0).toUpperCase() + category.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Features List */}
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Özellik Adı</th>
                      <th>Kod</th>
                      <th>Kategori</th>
                      <th>Durum</th>
                      <th>Gerekli Paket</th>
                      <th className="text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeatures.map((feature) => (
                      <tr key={feature.id}>
                        <td>
                          <div>
                            <div className="font-medium text-secondary-900">{feature.name}</div>
                            <div className="text-sm text-secondary-500">{feature.description}</div>
                          </div>
                        </td>
                        <td>
                          <code className="px-2 py-1 bg-gray-100 rounded text-xs text-secondary-600 border border-gray-200 font-mono">
                            {feature.code}
                          </code>
                        </td>
                        <td>
                          <span className="capitalize text-sm text-secondary-700">{feature.category}</span>
                        </td>
                        <td>
                          <span className={`badge ${feature.isActive
                              ? 'badge-success'
                              : 'badge-danger'
                            }`}>
                            {feature.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td>
                          <span className="capitalize badge badge-neutral">{feature.requiredPlan}</span>
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setEditingFeature(feature)}
                              className="btn btn-sm btn-ghost"
                              title="Düzenle"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteFeature(feature.id)}
                              className="btn btn-sm btn-ghost text-red-600 hover:bg-red-50"
                              title="Sil"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'dependencies' && (
            <div className="min-h-[500px]">
              <FeatureDependencyVisualization />
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingFeature) && (
        <FeatureModal
          feature={editingFeature}
          onClose={() => {
            setShowCreateModal(false)
            setEditingFeature(null)
          }}
          onSubmit={editingFeature ?
            (data) => handleUpdateFeature(editingFeature.id, data) :
            handleCreateFeature
          }
        />
      )}
    </div>
  )
}

const FeatureModal = ({ feature, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: feature?.name || '',
    code: feature?.code || '',
    description: feature?.description || '',
    category: feature?.category || 'core',
    isActive: feature?.isActive ?? true,
    isCore: feature?.isCore ?? false,
    requiredPlan: feature?.requiredPlan || 'free',
    sortOrder: feature?.sortOrder || 0,
    dependencies: feature?.dependencies || [],
    config: feature?.config || {},
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {feature ? 'Edit Feature' : 'Create Feature'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="input"
              >
                <option value="core">Core</option>
                <option value="advanced">Advanced</option>
                <option value="integration">Integration</option>
                <option value="reporting">Reporting</option>
                <option value="security">Security</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Plan
              </label>
              <select
                name="requiredPlan"
                value={formData.requiredPlan}
                onChange={handleChange}
                className="input"
              >
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="isCore"
                checked={formData.isCore}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Core Feature</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {feature ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Features 
